<script lang="ts">
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import window from '@@/bits/global'
import { stripHtml } from '@@/bits/html_parser'
import { __, p__ } from '@@/bits/intl'
import { isPostPinned, isPostPublished } from '@@/bits/post_state'
import type { TableColumn } from '@@/bits/table_layout'
import { isCustomPropertyTableColumn, shouldShowEndBorder } from '@@/bits/table_layout'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { editPost } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Id, Poll, Post } from '@@/types'
import SurfacePostAttachment from '@@/vuecomponents/SurfacePostAttachment.vue'
import SurfacePostAuthor from '@@/vuecomponents/SurfacePostAuthor.vue'
import SurfacePostBody from '@@/vuecomponents/SurfacePostBody.vue'
import SurfacePostCommentsIcon from '@@/vuecomponents/SurfacePostCommentsIcon.vue'
import SurfacePostCustomPropertyCell from '@@/vuecomponents/SurfacePostCustomPropertyCell.vue'
import SurfacePostPoll from '@@/vuecomponents/SurfacePostPoll.vue'
import SurfacePostReaction from '@@/vuecomponents/SurfacePostReaction.vue'
import { DEFAULT_COLUMN_WIDTH } from '@@/vuecomponents/SurfacePostsTable.vue'
import SurfacePostTableRowNumber from '@@/vuecomponents/SurfacePostTableRowNumber.vue'
import { useSurfaceAttachmentPicker } from '@@/vuecomposables/surface_attachment_picker'
import { useIntersectionCommentLoader } from '@@/vuecomposables/useIntersectionCommentLoader'
import { useClipboard } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

export const POST_ROW_DRAGGABLE_TYPE = 'post-row' as const

export const MIN_ROW_HEIGHT = 72
export default {}
</script>

<script setup lang="ts">
const props = defineProps<{
  post: Post
  postRowIndex: number
  allColumnsToShow: TableColumn[]
  reactionIdsByPostId: Record<Id, Id[]>
  commentIdsByPostId: Record<Id, Id[]>
  backgroundLuminance: 'light' | 'dark' // for padlet's wallpaper
  colorScheme: 'light' | 'dark' // user-defined settings
  hasScheduledBanner: boolean
  hasModerationBanner: boolean
  getPostBackgroundClass: (post: Post) => string | undefined // parent caches values across multiple posts, so pass getter from parent.
}>()

const surfaceStore = useSurfaceStore()
const { user } = storeToRefs(surfaceStore)
const expandedPostStore = useExpandedPostStore()
const { showPostActionMenu, setPostRightClickCoordinates } = useSurfacePostActionStore()
const reactionsStore = useReactionsStore()
const { accumulatedReactionsByWishId, originalReactionData, currentReactionData } = storeToRefs(reactionsStore)
const { isPreviewing } = storeToRefs(useSurfaceSettingsStore())
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { getDisplayedColorForPost } = surfacePostPropertiesStore
const { attachmentsEnabled } = storeToRefs(surfacePostPropertiesStore)
const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIModerate, canIWrite } = storeToRefs(surfacePermissionsStore)
const surfacePostsStore = useSurfacePostsStore()
const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())
const globalSnackbarStore = useGlobalSnackbarStore()
const { isGroupByAuthor } = storeToRefs(useSurfaceWishArrangementStore())

const containerRef = ref<HTMLTableRowElement | undefined>(undefined)

const isPublished = computed(() => isPostPublished(props.post))

// #region comments
useIntersectionCommentLoader({
  post: props.post,
  postElement: containerRef,
})

const commentIds = computed(() => props.commentIdsByPostId[Number(props.post.id)] || [])

const onShowComments = () => {
  expandedPostStore.expandPost({
    postCid: props.post.cid,
    xTextPanel: true,
    shouldFocusInput: true,
    shouldExpandMobilePanel: true,
  })
}
// #endregion

// #region post actions
const { pickContentSourceIfSupported } = useSurfaceAttachmentPicker()

const tryEditPost = () => {
  if (isPostEditableByCurrentUser.value) {
    surfacePostsStore.startEditingPost({ postCid: props.post.cid })
  }
}

const tryEditPostAndOpenContentPicker = () => {
  if (device.app) {
    postMessage(editPost(props.post, props.post.id, { openContentPicker: true }))
    return
  }
  if (isPostEditableByCurrentUser.value) {
    surfacePostsStore.startEditingPost({ postCid: props.post.cid })
    pickContentSourceIfSupported()
  }
}

const isPinned = computed(() => isPostPinned(props.post))

const isPostEditableByCurrentUser = computed<boolean>(() => {
  if (surfaceStore.isFrozen) return false

  const isCurrentUserLoggedIn = user.value.id != null
  const isCurrentUserTheAuthor = isCurrentUserLoggedIn && props.post.author_id === user.value.id
  const canCurrentUserEdit = isCurrentUserTheAuthor && canIWrite.value

  return canIModerate.value || canCurrentUserEdit
})

const onOpenPostContextMenu = (e: MouseEvent) => {
  setPostRightClickCoordinates({ postRightClickCoordinates: [e.pageX, e.pageY] })
  showPostActionMenu({ postCid: props.post.cid })
}
// #endregion

// #region click to copy cell value
const { copy, isSupported } = useClipboard()
const clickTimeout = ref<number | null>(null)
const lastClickTarget = ref<EventTarget | null>(null)

// Track clicks for manual double-click detection
const handleClick = (e: any, clickHandler?: () => Promise<void>) => {
  // Stop event propagation to prevent other handlers
  e.stopPropagation()

  // If there's an active timeout and this is a second click
  if (clickTimeout.value !== null) {
    // Clear the timeout to prevent the single-click action
    window.clearTimeout(clickTimeout.value)
    clickTimeout.value = null

    // If this is a second click on the same element, treat as double-click
    if (lastClickTarget.value === e.target) {
      tryEditPost() // double-click action is always edit post
    }

    lastClickTarget.value = null
    return
  }

  // Store the clicked element for comparison with second click
  lastClickTarget.value = e.target

  // If no second click is performed after delay, invoke given handler
  clickTimeout.value = window.setTimeout(async () => {
    if (clickHandler) {
      await clickHandler()
    }

    clickTimeout.value = null
    lastClickTarget.value = null
  }, 300)
}

const copySubject = async () => {
  if (!isSupported.value || !props.post.subject) return
  await copy(props.post.subject)
  globalSnackbarStore.setSnackbar({
    message: __('Copied subject'),
    notificationType: SnackbarNotificationType.success,
    timeout: 1000,
  })
}

const copyBody = async () => {
  if (!isSupported.value || !props.post.body) return
  await copy(stripHtml(props.post.body))
  globalSnackbarStore.setSnackbar({
    message: __('Copied body'),
    notificationType: SnackbarNotificationType.success,
    timeout: 1000,
  })
}

const copyLocation = async () => {
  if (!isSupported.value || !props.post.location_name) return
  await copy(props.post.location_name)
  globalSnackbarStore.setSnackbar({
    message: __('Copied location'),
    notificationType: SnackbarNotificationType.success,
    timeout: 1000,
  })
}

const copyCustomPropertyValue = async (event: { value: string; propertyName: string }) => {
  if (!isSupported.value || !event.value) return
  await copy(event.value)
  globalSnackbarStore.setSnackbar({
    message: p__('Copied value of specific custom property to clipboard', 'Copied %{propertyName}', {
      propertyName: event.propertyName,
    }),
    notificationType: SnackbarNotificationType.success,
    timeout: 1000,
  })
}

const handleCustomPropertyClick = (e: { event: any; value: string; propertyName: string }) => {
  const { event, value, propertyName } = e

  handleClick(event, async () => {
    await copyCustomPropertyValue({ value, propertyName })
  })
}
// #endregion

// #region post attachments/polls
const hasPollAttachment = computed(() => {
  return props.post?.wish_content?.attachment_props?.type === 'poll'
})

const pollForPost = computed(() => {
  return props.post?.wish_content?.attachment_props as Poll
})
// #endregion

// #region drag and drop

// Make table row draggable
const { isDragging } = useDraggableElement({
  elementRef: containerRef,
  type: POST_ROW_DRAGGABLE_TYPE,
  isDraggable: computed(() => !shouldDisableDraggingPost.value),
  data: computed(() => ({
    post: props.post,
  })),
})

// Make table row droppable
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef: containerRef,
  type: POST_ROW_DRAGGABLE_TYPE,
  data: computed(() => ({
    post: props.post,
  })),
})

// Add other rows as a source that can be dropped on this row
addDraggableSource({
  type: POST_ROW_DRAGGABLE_TYPE,
  axis: 'vertical',
  canDrop: ({ sourceItem, targetItem }) => {
    if (sourceItem.data.post.id === targetItem.data.post.id) return false
    // If group by author, posts can only be moved within the same author group
    if (isGroupByAuthor.value && sourceItem.data.post.author_id !== targetItem.data.post.author_id) return false
    return isPostPinned(sourceItem.data.post) === isPostPinned(targetItem.data.post)
  },
  onDrop: (e: OnDropPayload<{ post: Post }, { post: Post }>) => {
    const sourcePost = e.sourceItem.data.post
    const targetPost = e.targetItem.data.post

    // Find the target element
    const targetElement = document.querySelector(`#wish-${targetPost.id}`)
    const rowContainer = targetElement?.parentElement
    if (!targetElement || !rowContainer) return

    // Get the previous and next post IDs based on the drop position
    const { prevId, nextId } = (() => {
      if (e.relativePositionToTarget === 'before') {
        // We are dropping a row before this one. Find the previous post row and its ID.
        const prevRowContainer = rowContainer.previousElementSibling
        const prevPostRow = prevRowContainer?.querySelector('.post-table-row')
        return {
          prevId: prevPostRow?.id ?? null, // computePostIndexFromPost differentiates between null and undefined
          nextId: targetElement.id,
        }
      } else {
        // We are dropping a row after this one. Find the next post row and its ID.
        const nextRowContainer = rowContainer.nextElementSibling
        const nextPostRow = nextRowContainer?.querySelector('.post-table-row')
        return {
          prevId: targetElement.id,
          nextId: nextPostRow?.id ?? null, // computePostIndexFromPost differentiates between null and undefined
        }
      }
    })()

    const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${sourcePost.id}`, {
      prevId,
      nextId,
      sectionId: targetPost.wall_section_id,
    })

    // Make API call to update the post's sort_index
    surfacePostsStore.sortPost({
      postCid: sourcePost.cid,
      sortIndex: sortIndex ?? undefined,
      sectionId: targetPost.wall_section_id,
      gapSize,
    })
  },
})

// Adjust the drop indicator position when there are banners
const dropIndicatorStyles = computed(() => {
  if (closestEdgeToPointer.value === 'top' && props.hasScheduledBanner) {
    return {
      top: '-43px', // Position above the scheduled banner (banner height = 36px + spacing = 12px / 2 + 1px drop indicator thickness)
    }
  }
  if (closestEdgeToPointer.value === 'bottom' && props.hasModerationBanner) {
    return {
      bottom: '-47px', // Position below the moderation banner (banner height = 40px + spacing = 12px / 2 + 1px drop indicator thickness)
    }
  }
  return undefined
})
// #endregion

const reactions = computed(() => {
  const reactionData = isPreviewing.value ? currentReactionData.value : originalReactionData.value

  if (!props.post.id || !accumulatedReactionsByWishId.value[props.post.id])
    return {
      count: 0,
      type: reactionData?.type,
    }

  const type = accumulatedReactionsByWishId.value[props.post.id]?.reactionType
  const count = accumulatedReactionsByWishId.value[props.post.id]?.totalReactionsCount
  return {
    type,
    count,
  }
})
</script>

<template>
  <div
    :id="`wish-${post.id}`"
    ref="containerRef"
    :class="[
      'relative',
      'z-[1]', // render this above any scheduled post/moderation banners
      'post-table-row', // used by drag n drop sort index calculations
      'surface-post', // this class, together with data-post-cid are used by native bridge (post action menu)
      isDragging && 'opacity-50',
    ]"
    :data-pinned="isPinned"
    :data-rank="post.sort_index"
    :data-post-cid="post.cid"
    @contextmenu.prevent="onOpenPostContextMenu($event)"
  >
    <div
      class="flex flex-row box-border"
      :style="{ minHeight: `${MIN_ROW_HEIGHT}px` }"
      :data-post-color="getDisplayedColorForPost(post)"
      data-testid="surfacePostsTableRow"
    >
      <div
        v-for="(column, columnIndex) in allColumnsToShow"
        :key="column.field"
        dir="auto"
        :class="[
          'text-start',
          'break-words',
          column.field !== 'row_number' && 'p-1',
          'align-top',
          'box-border',
          shouldShowEndBorder(column, columnIndex, allColumnsToShow) && 'border-e-[0.5px] border-solid',
          colorScheme === 'dark' ? 'border-divider-gray-dark' : 'border-divider-gray',
          // Make row number and spacer cells transparent
          column.field !== 'row_number' &&
            column.field !== 'spacer' && [getPostBackgroundClass(post), 'shadow-elevation-1'],
          // Add rounded corners for cells at the start/end, or if they border 'empty' column types like spacer/row_number
          {
            'rounded-s-xl': !allColumnsToShow[columnIndex - 1]?.label,
            'rounded-e-xl': !allColumnsToShow[columnIndex + 1]?.label,
          },
          column.field === 'row_number' && 'sticky start-0 z-[11]',
          {
            hidden: !isPublished && (column.field === 'reactions' || column.field === 'comments'),
          },
          'transition-width duration-200',
        ]"
        :style="{ width: `${column.width}px` }"
        @click="handleClick($event)"
        @keydown.enter="() => {} /* no-op for linter */"
        @keydown.space="() => {} /* no-op for linter */"
      >
        <!-- #region render individual cell -->

        <!-- The row number column is going to be 36px wide, with the actual row number display being 24px wide. -->
        <SurfacePostTableRowNumber
          v-if="column.field === 'row_number'"
          :index="postRowIndex"
          :class="['h-full', 'shadow-elevation-1']"
          :background-luminance="backgroundLuminance"
          :post="post"
          :comment-count="commentIdsByPostId[Number(post.id)]?.length || 0"
          :reaction-count="reactions.count"
          :reaction-type="reactions.type"
          :is-published="isPublished"
          @show-comments="onShowComments"
        />

        <template v-else-if="column.field === 'author'">
          <SurfacePostAuthor
            class="p-1"
            :post="post"
            :avatar-size="16"
            :x-published-at="true"
            :dark-mode="colorScheme === 'dark'"
          />
        </template>

        <template v-else-if="column.field === 'subject'">
          <span
            v-if="post.subject"
            :class="[
              'inline-block',
              'text-body font-semibold',
              colorScheme === 'dark' ? 'text-light-text-100' : 'text-dark-text-100',
              'px-1',
              'cursor-pointer',
              'focus-visible:ring-2 focus-visible:rounded-lg',
              colorScheme === 'light' ? 'focus-visible:ring-grape-500' : 'focus-visible:ring-canary-500',
            ]"
            tabindex="0"
            :aria-label="__('Click to copy subject')"
            data-testid="surfacePostsTableRowSubject"
            @click="handleClick($event, copySubject)"
            @keydown.enter.prevent="copySubject"
            @keydown.space.prevent="copySubject"
            >{{ post.subject }}</span
          >
        </template>

        <template v-else-if="column.field === 'attachment'">
          <SurfacePostAttachment
            v-if="post.attachment"
            :dir="dir()"
            :post="post"
            :displayed-post-width="DEFAULT_COLUMN_WIDTH"
            :is-editable-by-current-user="isPostEditableByCurrentUser"
          />
          <SurfacePostPoll
            v-else-if="hasPollAttachment"
            :poll="pollForPost"
            :poll-caption="post.attachment_caption || ''"
            :post="post"
            :user="user"
            :dark-mode="colorScheme === 'dark'"
            :background-color="getDisplayedColorForPost(post)"
          />
          <div
            v-else-if="isPostEditableByCurrentUser && attachmentsEnabled"
            class="h-full w-full flex items-start justify-start"
          >
            <OzPlainButton
              :color-scheme="OzPlainButtonColorScheme.Secondary"
              :size-preset="OzPlainButtonSizePreset.H28px"
              :bold-text="true"
              :dark-mode="colorScheme === 'dark'"
              data-testid="surfacePostsTableRowAddAttachmentButton"
              @click="tryEditPostAndOpenContentPicker"
            >
              <span class="gap-x-1 flex items-start">
                <OzIcon name="plus_thick" :size="14" class="inline-flex pt-0.5" /> {{ __('Add attachment...') }}
              </span>
            </OzPlainButton>
          </div>
        </template>

        <template v-else-if="column.field === 'body'">
          <SurfacePostBody
            v-if="post.body"
            :content="post.body"
            :dark-mode="colorScheme === 'dark'"
            :class="[
              'px-1 text-body-small-posts',
              'cursor-pointer',
              'focus-visible:ring-2 focus-visible:rounded-lg',
              colorScheme === 'light' ? 'focus-visible:ring-grape-500' : 'focus-visible:ring-canary-500',
            ]"
            tabindex="0"
            :aria-label="__('Click to copy body')"
            @click="handleClick($event, copyBody)"
            @keydown.enter.prevent="copyBody"
            @keydown.space.prevent="copyBody"
          />
        </template>

        <template v-else-if="column.field === 'location'">
          <span
            v-if="post.location_name"
            :class="[
              'text-body-small-posts',
              colorScheme === 'dark' ? 'text-light-text-100' : 'text-dark-text-100',
              'block px-1',
              'cursor-pointer',
              'focus-visible:ring-2 focus-visible:rounded-lg',
              colorScheme === 'light' ? 'focus-visible:ring-grape-500' : 'focus-visible:ring-canary-500',
            ]"
            tabindex="0"
            :aria-label="__('Click to copy location')"
            data-testid="surfacePostsTableRowLocation"
            @click="handleClick($event, copyLocation)"
            @keydown.enter.prevent="copyLocation"
            @keydown.space.prevent="copyLocation"
          >
            {{ post.location_name }}
          </span>
        </template>

        <template v-else-if="isCustomPropertyTableColumn(column)">
          <div class="px-1">
            <SurfacePostCustomPropertyCell
              :post="post"
              :property="column.customProperty"
              :dark-mode="colorScheme === 'dark'"
              data-testid="surfacePostsTableRowCustomProperty"
              @click="handleCustomPropertyClick($event)"
              @copy-value="copyCustomPropertyValue($event)"
            />
          </div>
        </template>

        <template v-else-if="column.field === 'reactions'">
          <div class="py-4 px-2 flex justify-center">
            <SurfacePostReaction
              v-if="post.id"
              :post="post"
              :is-reacting="reactionIdsByPostId[post.id]?.length > 0"
              :current-user-id="user.id"
              :reaction-data="isPreviewing ? currentReactionData : originalReactionData"
              :color-scheme="colorScheme === 'dark' ? 'dark' : 'light'"
              usage-context="postView"
            />
          </div>
        </template>

        <template v-else-if="column.field === 'comments'">
          <div class="py-3 px-2 flex justify-center">
            <SurfacePostCommentsIcon
              :comment-ids="commentIds"
              :color-scheme="colorScheme === 'dark' ? 'dark' : 'light'"
              :is-button="true"
              usage-context="postView"
              @show-comments-panel="onShowComments"
            />
          </div>
        </template>

        <!-- #endregion -->
      </div>
    </div>

    <OzDropIndicatorLine
      v-if="closestEdgeToPointer"
      :edge="closestEdgeToPointer"
      :gap="12"
      :dark-mode="backgroundLuminance === 'dark'"
      :style="dropIndicatorStyles"
      class="absolute inset-x-0"
    />
  </div>
</template>
